import {
  PREVENTION_SCOPE_TYPES,
  PREVENTION_TARGET_MATCH_KINDS,
  PREVENTION_WAF_VENDORS,
  type CreatePreventionTargetMappingBody,
  type PreventionConnectionDto,
  type PreventionTargetMappingDto,
  type PreventionWafSuggestion,
  type PreventionWafVendor,
} from '@terra/shared-contracts-integrations';

export const PREVENTION_ROUTING_TARGETS = {
  CLOUDFLARE: 'cloudflare',
  VERCEL: 'vercel',
  AWS_WAFV2: 'awsWafv2',
} as const;

export type PreventionRoutingTarget =
  (typeof PREVENTION_ROUTING_TARGETS)[keyof typeof PREVENTION_ROUTING_TARGETS];

export type PreventionRoutingDraft = {
  selectedTarget: PreventionRoutingTarget | null;
  selectedConnectionId: string | null;
  zoneId: string | undefined;
  projectId: string | undefined;
  webAclArn: string | undefined;
};

export const EMPTY_PREVENTION_ROUTING_DRAFT: PreventionRoutingDraft = {
  selectedTarget: null,
  selectedConnectionId: null,
  zoneId: undefined,
  projectId: undefined,
  webAclArn: undefined,
};

export type PreventionDiscoveryRequest = {
  vendor: PreventionWafVendor;
  connectionId: string | undefined;
  enabled: boolean;
};

export const toPreventionRoutingTarget = (
  vendor: string | null | undefined,
): PreventionRoutingTarget | null => {
  if (vendor === PREVENTION_WAF_VENDORS.AWS_WAFV2) {
    return PREVENTION_ROUTING_TARGETS.AWS_WAFV2;
  }

  if (vendor === PREVENTION_WAF_VENDORS.VERCEL) {
    return PREVENTION_ROUTING_TARGETS.VERCEL;
  }

  if (vendor === PREVENTION_WAF_VENDORS.CLOUDFLARE) {
    return PREVENTION_ROUTING_TARGETS.CLOUDFLARE;
  }

  return null;
};

export const filterRoutableConnections = ({
  connections,
  vendor,
}: {
  connections: readonly PreventionConnectionDto[];
  vendor: PreventionWafVendor;
}): PreventionConnectionDto[] =>
  connections.filter(
    (connection) =>
      connection.vendor === vendor &&
      connection.status === 'VALID' &&
      connection.stagePreventionEnabled,
  );

export const findApplicationMapping = ({
  mappings,
  applicationId,
}: {
  mappings: readonly PreventionTargetMappingDto[];
  applicationId: string;
}): PreventionTargetMappingDto | null =>
  mappings.find(
    (mapping) =>
      mapping.matchKind === PREVENTION_TARGET_MATCH_KINDS.APPLICATION &&
      mapping.matchValue === applicationId,
  ) ?? null;

export const buildRoutingTargetOptions = ({
  hasCloudflare,
  hasVercel,
  isAwsPreventionReady,
}: {
  hasCloudflare: boolean;
  hasVercel: boolean;
  isAwsPreventionReady: boolean;
}): PreventionRoutingTarget[] => [
  ...(hasCloudflare ? [PREVENTION_ROUTING_TARGETS.CLOUDFLARE] : []),
  ...(hasVercel ? [PREVENTION_ROUTING_TARGETS.VERCEL] : []),
  ...(isAwsPreventionReady ? [PREVENTION_ROUTING_TARGETS.AWS_WAFV2] : []),
];

export type PreventionRoutingState = {
  effectiveTarget: PreventionRoutingTarget | null;
  effectiveConnectionId: string;
  effectiveZoneId: string;
  effectiveProjectId: string;
  effectiveWebAclArn: string;
  canSave: boolean;
};

const resolveSelectedConnectionId = ({
  draft,
  effectiveTarget,
  cloudflareConnections,
  vercelConnections,
}: {
  draft: PreventionRoutingDraft;
  effectiveTarget: PreventionRoutingTarget | null;
  cloudflareConnections: readonly PreventionConnectionDto[];
  vercelConnections: readonly PreventionConnectionDto[];
}): string | null => {
  const { selectedConnectionId } = draft;

  if (!selectedConnectionId) {
    return null;
  }

  const candidates =
    effectiveTarget === PREVENTION_ROUTING_TARGETS.CLOUDFLARE
      ? cloudflareConnections
      : effectiveTarget === PREVENTION_ROUTING_TARGETS.VERCEL
        ? vercelConnections
        : [];

  return candidates.some((connection) => connection.id === selectedConnectionId)
    ? selectedConnectionId
    : null;
};

const resolveVendorConnectionId = ({
  target,
  effectiveTarget,
  currentConnection,
  connections,
}: {
  target: PreventionRoutingTarget;
  effectiveTarget: PreventionRoutingTarget | null;
  currentConnection: PreventionConnectionDto | null;
  connections: readonly PreventionConnectionDto[];
}): { mapped: string | null; sole: string | null } => {
  if (effectiveTarget !== target) {
    return { mapped: null, sole: null };
  }

  const mapped =
    currentConnection &&
    toPreventionRoutingTarget(currentConnection.vendor) === target
      ? currentConnection.id
      : null;
  const sole = connections.length === 1 ? (connections[0]?.id ?? null) : null;

  return { mapped, sole };
};

export const resolvePreventionRoutingState = ({
  draft,
  defaultTarget,
  currentMapping,
  currentConnection,
  cloudflareConnections,
  vercelConnections,
}: {
  draft: PreventionRoutingDraft;
  defaultTarget: PreventionRoutingTarget | null;
  currentMapping: PreventionTargetMappingDto | null;
  currentConnection: PreventionConnectionDto | null;
  cloudflareConnections: readonly PreventionConnectionDto[];
  vercelConnections: readonly PreventionConnectionDto[];
}): PreventionRoutingState => {
  const effectiveTarget = draft.selectedTarget ?? defaultTarget;
  const cloudflare = resolveVendorConnectionId({
    target: PREVENTION_ROUTING_TARGETS.CLOUDFLARE,
    effectiveTarget,
    currentConnection,
    connections: cloudflareConnections,
  });
  const vercel = resolveVendorConnectionId({
    target: PREVENTION_ROUTING_TARGETS.VERCEL,
    effectiveTarget,
    currentConnection,
    connections: vercelConnections,
  });
  const effectiveConnectionId =
    resolveSelectedConnectionId({
      draft,
      effectiveTarget,
      cloudflareConnections,
      vercelConnections,
    }) ??
    cloudflare.mapped ??
    vercel.mapped ??
    cloudflare.sole ??
    vercel.sole ??
    '';
  const currentTarget = toPreventionRoutingTarget(currentConnection?.vendor);
  const savedScopeRef = currentMapping?.scopeRef ?? '';
  const usesSavedConnection =
    Boolean(currentMapping) &&
    currentMapping?.connectionId === effectiveConnectionId;
  const savedCloudflareScopeRef =
    usesSavedConnection &&
    currentTarget === PREVENTION_ROUTING_TARGETS.CLOUDFLARE
      ? savedScopeRef
      : '';
  const savedVercelScopeRef =
    usesSavedConnection && currentTarget === PREVENTION_ROUTING_TARGETS.VERCEL
      ? savedScopeRef
      : '';
  const savedAwsScopeRef =
    currentTarget === PREVENTION_ROUTING_TARGETS.AWS_WAFV2 ? savedScopeRef : '';
  const effectiveZoneId = draft.zoneId ?? savedCloudflareScopeRef;
  const effectiveProjectId = draft.projectId ?? savedVercelScopeRef;
  const effectiveWebAclArn = draft.webAclArn ?? savedAwsScopeRef;
  const savedConnectionIdFor = (target: PreventionRoutingTarget) =>
    currentTarget === target ? (currentConnection?.id ?? '') : '';

  const hasRequiredValue =
    effectiveTarget === PREVENTION_ROUTING_TARGETS.CLOUDFLARE
      ? Boolean(effectiveConnectionId && effectiveZoneId.trim())
      : effectiveTarget === PREVENTION_ROUTING_TARGETS.VERCEL
        ? Boolean(effectiveConnectionId && effectiveProjectId.trim())
        : effectiveTarget === PREVENTION_ROUTING_TARGETS.AWS_WAFV2
          ? Boolean(effectiveWebAclArn.trim())
          : false;
  const isDirty =
    effectiveTarget === PREVENTION_ROUTING_TARGETS.CLOUDFLARE
      ? effectiveConnectionId !==
          savedConnectionIdFor(PREVENTION_ROUTING_TARGETS.CLOUDFLARE) ||
        effectiveZoneId.trim() !== savedCloudflareScopeRef.trim()
      : effectiveTarget === PREVENTION_ROUTING_TARGETS.VERCEL
        ? effectiveConnectionId !==
            savedConnectionIdFor(PREVENTION_ROUTING_TARGETS.VERCEL) ||
          effectiveProjectId.trim() !== savedVercelScopeRef.trim()
        : effectiveTarget === PREVENTION_ROUTING_TARGETS.AWS_WAFV2
          ? effectiveWebAclArn.trim() !== savedAwsScopeRef.trim()
          : false;

  return {
    effectiveTarget,
    effectiveConnectionId,
    effectiveZoneId,
    effectiveProjectId,
    effectiveWebAclArn,
    canSave: hasRequiredValue && isDirty,
  };
};

export const isPreventionSuggestionApplied = ({
  suggestion,
  state,
  currentMapping,
  currentConnection,
}: {
  suggestion: PreventionWafSuggestion | null;
  state: PreventionRoutingState;
  currentMapping: PreventionTargetMappingDto | null;
  currentConnection: PreventionConnectionDto | null;
}): boolean => {
  const suggestionTarget = toPreventionRoutingTarget(suggestion?.vendor);

  if (!suggestion || !suggestionTarget) {
    return false;
  }

  if (state.effectiveTarget !== suggestionTarget) {
    return false;
  }

  const suggestedScope = suggestion.suggestedScopeRef;

  if (!suggestedScope) {
    return (
      Boolean(currentMapping) && currentConnection?.vendor === suggestion.vendor
    );
  }

  if (suggestionTarget === PREVENTION_ROUTING_TARGETS.CLOUDFLARE) {
    return state.effectiveZoneId === suggestedScope;
  }

  if (suggestionTarget === PREVENTION_ROUTING_TARGETS.VERCEL) {
    return state.effectiveProjectId === suggestedScope;
  }

  return state.effectiveWebAclArn === suggestedScope;
};

export const resolveCloudflareConnectionForSuggestion = ({
  suggestion,
  connections,
  currentConnectionId,
}: {
  suggestion: PreventionWafSuggestion;
  connections: readonly PreventionConnectionDto[];
  currentConnectionId: string | undefined;
}): PreventionConnectionDto | undefined => {
  const { suggestedScopeRef, hostname } = suggestion;
  const matchesScope = suggestedScopeRef
    ? connections.find((connection) =>
        connection.coverage.some((item) => item.scopeRef === suggestedScopeRef),
      )
    : undefined;
  const matchesHost = hostname
    ? connections.find((connection) =>
        connection.coverage.some((item) => item.value === hostname),
      )
    : undefined;
  const existing = connections.find(
    (connection) => connection.id === currentConnectionId,
  );

  return matchesScope ?? matchesHost ?? existing ?? connections[0];
};

export const resolveVercelConnectionForSuggestion = ({
  connections,
  currentConnectionId,
}: {
  connections: readonly PreventionConnectionDto[];
  currentConnectionId: string | undefined;
}): PreventionConnectionDto | undefined =>
  connections.find((connection) => connection.id === currentConnectionId) ??
  connections[0];

export const findRecommendedWebAclArn = (
  suggestions: readonly PreventionWafSuggestion[],
): string | null =>
  suggestions.find(
    (suggestion) =>
      suggestion.vendor === PREVENTION_WAF_VENDORS.AWS_WAFV2 &&
      Boolean(suggestion.suggestedScopeRef),
  )?.suggestedScopeRef ?? null;

export const resolvePreventionDiscoveryRequest = ({
  state,
  isAwsPreventionReady,
}: {
  state: PreventionRoutingState;
  isAwsPreventionReady: boolean;
}): PreventionDiscoveryRequest | null => {
  if (state.effectiveTarget === PREVENTION_ROUTING_TARGETS.CLOUDFLARE) {
    return {
      vendor: PREVENTION_WAF_VENDORS.CLOUDFLARE,
      connectionId: state.effectiveConnectionId,
      enabled: Boolean(state.effectiveConnectionId),
    };
  }

  if (state.effectiveTarget === PREVENTION_ROUTING_TARGETS.VERCEL) {
    return {
      vendor: PREVENTION_WAF_VENDORS.VERCEL,
      connectionId: state.effectiveConnectionId,
      enabled: Boolean(state.effectiveConnectionId),
    };
  }

  if (state.effectiveTarget === PREVENTION_ROUTING_TARGETS.AWS_WAFV2) {
    return {
      vendor: PREVENTION_WAF_VENDORS.AWS_WAFV2,
      connectionId: undefined,
      enabled: isAwsPreventionReady,
    };
  }

  return null;
};

export const buildConnectionMappingBody = ({
  applicationId,
  state,
}: {
  applicationId: string;
  state: PreventionRoutingState;
}): CreatePreventionTargetMappingBody | null => {
  if (state.effectiveTarget === PREVENTION_ROUTING_TARGETS.CLOUDFLARE) {
    const zoneId = state.effectiveZoneId.trim();

    return state.effectiveConnectionId && zoneId
      ? {
          matchKind: PREVENTION_TARGET_MATCH_KINDS.APPLICATION,
          matchValue: applicationId,
          connectionId: state.effectiveConnectionId,
          scopeRef: zoneId,
          scopeType: PREVENTION_SCOPE_TYPES.ZONE,
        }
      : null;
  }

  if (state.effectiveTarget === PREVENTION_ROUTING_TARGETS.VERCEL) {
    const projectId = state.effectiveProjectId.trim();

    return state.effectiveConnectionId && projectId
      ? {
          matchKind: PREVENTION_TARGET_MATCH_KINDS.APPLICATION,
          matchValue: applicationId,
          connectionId: state.effectiveConnectionId,
          scopeRef: projectId,
          scopeType: PREVENTION_SCOPE_TYPES.FIREWALL,
        }
      : null;
  }

  return null;
};

export const buildAwsWebAclMappingBody = ({
  applicationId,
  connectionId,
  webAclArn,
}: {
  applicationId: string;
  connectionId: string;
  webAclArn: string;
}): CreatePreventionTargetMappingBody => ({
  matchKind: PREVENTION_TARGET_MATCH_KINDS.APPLICATION,
  matchValue: applicationId,
  connectionId,
  scopeRef: webAclArn.trim(),
  scopeType: PREVENTION_SCOPE_TYPES.WEB_ACL,
});
